/**
 * Per-day working-hours editor (multi-day projects).
 *
 * Companion to CalendarDatePicker: given the selected day range, captures the
 * crew's working window per day — either the *same* hours every day or
 * *different* hours per day. Emits a ScheduleDay[] covering the selected days;
 * reconciles itself when the range changes (preserving existing per-day times,
 * filling new days from the first day's window).
 *
 * Weekends are skipped when auto-filling a range — Vari crews work Mon–Fri
 * unless the supervisor explicitly opts a weekend in (the "Include weekend
 * days" toggle, shown only when the range spans one). Without this, a
 * Fri–Mon range silently baked Sat+Sun rows into the schedule and the
 * approve flow seeded real task times onto them.
 */
import { useEffect, useMemo, useState } from 'react';
import type { ScheduleDay } from '@vari-crew/schemas';
import { Label } from '@/components/ui/Input';
import { parseDay, toDayStr } from '@/lib/calendarGrid';

const DEFAULT_START = '08:00';
const DEFAULT_END = '17:00';
const INPUT_CLS = 'h-9 rounded-md border border-line bg-transparent px-2 text-sm';

export function isWeekendDay(date: string): boolean {
  const dow = parseDay(date).getDay(); // 0=Sun … 6=Sat
  return dow === 0 || dow === 6;
}

function daysInRange(
  start: string | null,
  end: string | null,
  includeWeekends: boolean,
): string[] {
  if (!start) return [];
  const last = end && end >= start ? end : start;
  const out: string[] = [];
  let d = parseDay(start);
  const lastD = parseDay(last);
  let guard = 0;
  while (d <= lastD && guard < 60) {
    out.push(toDayStr(d));
    d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
    guard += 1;
  }
  if (includeWeekends) return out;
  const weekdays = out.filter((day) => !isWeekendDay(day));
  // An all-weekend range (rare, but pickable) still needs working days.
  return weekdays.length > 0 ? weekdays : out;
}

function dayLabel(date: string): string {
  return parseDay(date).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

export function DailyHoursPicker({
  start,
  end,
  value,
  onChange,
  flexible,
  onFlexibleChange,
  includeWeekends,
}: {
  start: string | null;
  end: string | null;
  value: ScheduleDay[] | null;
  onChange: (schedule: ScheduleDay[] | null) => void;
  /** When true, the PM is leaving the timing flexible ("come whenever"); the
   *  day windows still carry advisory placeholder hours the supervisor firms up.
   *  Optional: the "Flexible" choice only appears when onFlexibleChange is given
   *  (the supervisor's commit / resubmit pickers don't offer it). */
  flexible?: boolean;
  onFlexibleChange?: (flexible: boolean) => void;
  /** Controlled from the calendar's weekend toggle — when false (default), Sat/Sun
   *  are dropped from the generated schedule (Vari crews work Mon–Fri). */
  includeWeekends?: boolean;
}) {
  const showFlexible = !!onFlexibleChange;
  const flex = (flexible ?? false) && showFlexible;
  const days = useMemo(
    () => daysInRange(start, end, includeWeekends ?? false),
    [start, end, includeWeekends],
  );
  const multi = days.length > 1;

  const [mode, setMode] = useState<'same' | 'perday'>(() => {
    const v = value ?? [];
    if (v.length <= 1) return 'same';
    const f = v[0]!;
    return v.every((e) => e.start_time === f.start_time && e.end_time === f.end_time)
      ? 'same'
      : 'perday';
  });

  // Reconcile the schedule to cover exactly the selected days when the range
  // changes — keep existing per-day times, fill new days from the first day's
  // window. The JSON guard keeps edits (which don't change `days`) from looping.
  useEffect(() => {
    if (days.length === 0) {
      if (value && value.length) onChange(null);
      return;
    }
    const byDate = new Map((value ?? []).map((s) => [s.date, s]));
    const ref = value?.[0];
    const refStart = ref?.start_time ?? DEFAULT_START;
    const refEnd = ref?.end_time ?? DEFAULT_END;
    const next: ScheduleDay[] = days.map(
      (d) => byDate.get(d) ?? { date: d, start_time: refStart, end_time: refEnd },
    );
    if (JSON.stringify(next) !== JSON.stringify(value ?? [])) onChange(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days.join('|')]);

  if (days.length === 0) return null;

  const schedule = value ?? [];
  const first =
    schedule[0] ?? { date: days[0]!, start_time: DEFAULT_START, end_time: DEFAULT_END };

  function setAll(field: 'start_time' | 'end_time', v: string) {
    onChange(days.map((d) => ({ ...first, date: d, [field]: v })));
  }
  function setDay(date: string, field: 'start_time' | 'end_time', v: string) {
    onChange(schedule.map((s) => (s.date === date ? { ...s, [field]: v } : s)));
  }
  function switchMode(m: 'same' | 'perday') {
    setMode(m);
    if (m === 'same') {
      onChange(
        days.map((d) => ({ date: d, start_time: first.start_time, end_time: first.end_time })),
      );
    }
  }

  return (
    <div className="space-y-2">
      <Label>Working hours (allowed on site)</Label>
      {(multi || showFlexible) && (
        <div className="flex flex-wrap gap-4 text-xs text-ink-dim">
          {multi && (
            <>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="radio"
                  checked={!flex && mode === 'same'}
                  onChange={() => {
                    onFlexibleChange?.(false);
                    switchMode('same');
                  }}
                />
                Same hours every day
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="radio"
                  checked={!flex && mode === 'perday'}
                  onChange={() => {
                    onFlexibleChange?.(false);
                    switchMode('perday');
                  }}
                />
                Different per day
              </label>
            </>
          )}
          {!multi && showFlexible && (
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="radio" checked={!flex} onChange={() => onFlexibleChange?.(false)} />
              Specific time
            </label>
          )}
          {showFlexible && (
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="radio" checked={flex} onChange={() => onFlexibleChange?.(true)} />
              Flexible (come whenever)
            </label>
          )}
        </div>
      )}
      {flex ? (
        <p className="text-xs text-ink-faint">
          Timing is flexible — the crew will coordinate exact hours with the site
          contact.
        </p>
      ) : !multi || mode === 'same' ? (
        <div className="flex items-center gap-1.5">
          <input
            aria-label="Start time"
            type="time"
            value={first.start_time}
            onChange={(e) => setAll('start_time', e.target.value)}
            className={INPUT_CLS}
          />
          <span className="text-ink-faint text-xs">to</span>
          <input
            aria-label="End time"
            type="time"
            value={first.end_time}
            onChange={(e) => setAll('end_time', e.target.value)}
            className={INPUT_CLS}
          />
        </div>
      ) : (
        <div className="space-y-1.5">
          {schedule.map((s) => (
            <div key={s.date} className="flex items-center gap-2">
              <span className="text-xs text-ink-dim w-24 shrink-0">{dayLabel(s.date)}</span>
              <input
                aria-label={`Start time ${s.date}`}
                type="time"
                value={s.start_time}
                onChange={(e) => setDay(s.date, 'start_time', e.target.value)}
                className={INPUT_CLS}
              />
              <span className="text-ink-faint text-xs">to</span>
              <input
                aria-label={`End time ${s.date}`}
                type="time"
                value={s.end_time}
                onChange={(e) => setDay(s.date, 'end_time', e.target.value)}
                className={INPUT_CLS}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
